import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useMemo, useId, type FC } from "react";
import type { WorkflowEditFormTriggerType } from "@/components/automations/automation-edit-form-data";
import { useFeatureAutomations, AutomationsCapabilities } from "@/hooks/use-feature-automations";
import IconPlayCircle from "central-icons/IconPlayCircle";
import IconCalendarClock4 from "central-icons/IconCalendarClock4";
import IconPullRequest from "central-icons/IconPullRequest";

type TriggerTypeSelectProps = {
    value: WorkflowEditFormTriggerType;
    onChange: (value: WorkflowEditFormTriggerType) => void;
    disabled?: boolean;
};

type TriggerTypeOption = {
    icon: typeof IconPlayCircle;
    value: WorkflowEditFormTriggerType;
    label: string;
    description: string;
    enabled: boolean;
    disabledReason?: string;
};

const baseTriggerTypeOptions: Omit<TriggerTypeOption, "enabled" | "disabledReason">[] = [
    {
        icon: IconPlayCircle,
        value: "manual",
        label: "Manual",
        description: "Triggered manually by user action",
    },
    {
        icon: IconPullRequest,
        value: "pullRequest",
        label: "Pull Request",
        description: "Triggered by pull request events",
    },
    {
        icon: IconCalendarClock4,
        value: "time",
        label: "Scheduled",
        description: "Triggered automatically on a schedule",
    },
];

export const TriggerTypeSelect: FC<TriggerTypeSelectProps> = ({ value, onChange, disabled = false }) => {
    const selectId = useId();
    const { capabilities } = useFeatureAutomations();

    const triggerTypeOptions: TriggerTypeOption[] = useMemo(
        () =>
            baseTriggerTypeOptions.map((option) => {
                if (option.value === "pullRequest") {
                    const hasCap = capabilities.has(AutomationsCapabilities.Webhooks);
                    return {
                        ...option,
                        enabled: hasCap,
                        disabledReason: hasCap ? undefined : "Not available on your plan",
                    };
                }
                return { ...option, enabled: true };
            }),
        [capabilities],
    );

    const selected = triggerTypeOptions.find((option) => option.value === value);

    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger id={selectId} loading={false} className="w-fit px-3">
                <SelectValue placeholder="Select trigger type">
                    {selected && (
                        <div className="flex items-center gap-2">
                            {<selected.icon size={16} className="text-content-primary" />}
                            <span>{selected.label} trigger</span>
                        </div>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {triggerTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} disabled={!option.enabled}>
                        <div className="flex items-center gap-2 px-2 py-1.5">
                            {<option.icon size={16} className="text-content-primary" />}
                            <div className="flex flex-col items-start gap-0.5">
                                <span>{option.label}</span>
                                <span className="text-xs text-content-strong">
                                    {option.description}
                                    {!option.enabled && option.disabledReason && (
                                        <span> - {option.disabledReason}</span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};
